import React, {useEffect, useState} from 'react';
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import customFonts from '../fontsPDF/fonts';
import background from '../vfs';
import headerImage from '../headerImage';
import {useAuth} from '../AuthContext';
import axios from "axios";
import he from 'he';
import API_URL from '../config/apiConfig';
import Swal from "sweetalert2";

// --- CONFIGURATION DES POLICES ---
const standardFonts = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
pdfMake.vfs = { ...standardFonts, ...(customFonts || {}) };
if (background && background.background) {
    pdfMake.vfs['background.jpg'] = background.background;
}
pdfMake.fonts = {
    Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf'
    },
    Noto_Sans: {
        normal: 'NotoSans-Regular.ttf',
        bold: 'NotoSans-Bold.ttf',
        italics: 'NotoSans-Italic.ttf',
        bolditalics: 'NotoSans-BoldItalic.ttf'
    }
};

// --- UTILITAIRES ---

const cleanText = (text) => {
    if (!text || typeof text !== 'string') return text;
    return he.decode(text)
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')  // Enlever aussi les * simples (italique markdown)
        .replace(/#{1,6}\s*/g, '') // Enlever les # markdown residuels
        .replace(/([0-9]+)( ?)cm/g, '$1 cm')
        .replace(/cm([a-zA-Z])/g, 'cm $1')
        .replace(/([0-9]+)°/g, '$1 °')
        .replace(/\u2022/g, '\u2022')
        .replace(/\u2013/g, '-')
        .replace(/\u2014/g, '-')
        .replace(/;/g, '.')
        .trim();
};

/** Extraire le texte brut d'un message (gère string et array) */
const getMessageText = (msg) => {
    if (!msg || !msg.content) return '';
    if (typeof msg.content === 'string') return msg.content;
    if (Array.isArray(msg.content)) {
        return msg.content
            .filter(c => c.type === 'text' && c.text)
            .map(c => c.text)
            .join(' ');
    }
    return '';
};

/** Extraire les infos cles depuis les messages de la conversation.
 *  Priorite aux messages assistant (ou l'IA confirme les infos) */
const extractInfoFromMessages = (messages) => {
    let clientName = '';
    let mandant = '';
    let partieAdverse = '';
    let dommage = '';

    const clientPatterns = [
        /client\s*(?:\/\s*assur[ée]e?)?\s*[:]\s*([^\n,.;(]+)/i,
        /votre client[,\s]+([A-ZÀ-Ÿ][a-zà-ÿ]+(?:\s+[A-ZÀ-Ÿ][a-zà-ÿ]+)+)/i,
        /repr[ée]senter\s+(?:votre\s+)?client[,\s]+([A-ZÀ-Ÿ][a-zà-ÿ]+(?:\s+[A-ZÀ-Ÿ][a-zà-ÿ]+)+)/i,
        /assur[ée]e?\s*[:]\s*([^\n,.;(]+)/i,
    ];
    const mandantPatterns = [
        /mandant\s*[:]\s*([^\n,.;(]+)/i,
        /mandat[ée]e?\s+par\s+([^\n,.;(]+)/i,
    ];
    const adversePatterns = [
        /partie\s+adverse\s*[:]\s*([^\n,.;(]+)/i,
        /adverse\s*[:]\s*([^\n,.;(]+)/i,
        /opposant\s+[àa]\s+([^\n,.;(]+)/i,
    ];
    const dommagePatterns = [
        /(?:dommage|sinistre|d[ée]sordre|d[ée]g[aâ]t)\s*(?:des?\s*)?(?:eaux?|incendie|fissur|infiltrat|humidit[ée]|inondation|malfaçon|toiture|fa[cç]ade)/i,
        /(?:d[ée]g[aâ]t(?:s)?\s+des\s+eaux)/i,
        /(?:infiltration|fissure|humidit[ée]|malfaçon|sinistre|inondation)/i,
        /(?:type\s+de\s+)?(?:dommage|sinistre)\s*[:]\s*([^\n,.;(]+)/i,
    ];

    const tryMatch = (clean, patterns) => {
        for (const p of patterns) {
            const m = clean.match(p);
            if (m && (m[1] || m[0])) return (m[1] || m[0]).trim();
        }
        return '';
    };

    // 1er passage : messages assistant uniquement (la ou l'IA confirme les infos)
    for (const msg of messages) {
        if (msg.role !== 'assistant') continue;
        const rawText = getMessageText(msg);
        if (!rawText) continue;
        const clean = rawText.replace(/\*{1,3}/g, '').replace(/#{1,6}\s*/g, '');

        if (!clientName) { const v = tryMatch(clean, clientPatterns); if (v.length > 2) clientName = v; }
        if (!mandant) mandant = tryMatch(clean, mandantPatterns);
        if (!partieAdverse) partieAdverse = tryMatch(clean, adversePatterns);
        if (!dommage) {
            const v = tryMatch(clean, dommagePatterns);
            if (v) dommage = v.charAt(0).toUpperCase() + v.slice(1);
        }
        if (clientName && mandant && partieAdverse && dommage) break;
    }

    // 2eme passage : messages user en fallback si pas trouve
    if (!clientName || !dommage) {
        for (const msg of messages) {
            if (msg.role === 'assistant') continue;
            const rawText = getMessageText(msg);
            if (!rawText) continue;
            const clean = rawText.replace(/\*{1,3}/g, '').replace(/#{1,6}\s*/g, '');

            if (!clientName) { const v = tryMatch(clean, clientPatterns); if (v.length > 2) clientName = v; }
            if (!dommage) {
                const v = tryMatch(clean, dommagePatterns);
                if (v) dommage = v.charAt(0).toUpperCase() + v.slice(1);
            }
            if (clientName && dommage) break;
        }
    }

    return { clientName, mandant, partieAdverse, dommage };
};

/** Transformer le contenu brut du rapport en sections structurees pour pdfmake */
const buildStructuredContent = (rawContent) => {
    const lines = rawContent.split('\n').filter(l => l.trim());
    const content = [];

    const mainSectionRegex = /^(I{1,3}V?|VI{0,3}|VII?)\.\s+(.+)/;
    const subSectionRegex = /^([A-D])\.\s+(.+)/;
    const numberedRegex = /^([0-9]+)\.\s+(.+)/;

    let i = 0;
    while (i < lines.length) {
        const line = lines[i].trim();
        if (!line) { i++; continue; }

        const mainMatch = line.match(mainSectionRegex);
        const subMatch = line.match(subSectionRegex);
        const numMatch = line.match(numberedRegex);

        if (mainMatch) {
            content.push({ text: line, style: 'sectionTitle' });
            content.push({
                canvas: [{ type: 'line', x1: 0, y1: 0, x2: 395, y2: 0, lineWidth: 1, lineColor: '#Ceaa5b' }],
                margin: [0, 0, 0, 6],
            });
        } else if (subMatch || numMatch) {
            // Sous-section (A. B. C.) ou liste numerotee (1. 2. 3.)
            // Le prefixe est en gras, le reste en normal
            const prefix = subMatch ? `${subMatch[1]}. ` : `${numMatch[1]}. `;
            const rest = subMatch ? subMatch[2] : numMatch[2];
            // Si le texte contient un ":", mettre le label avant ":" en gras aussi
            const colonIdx = rest.indexOf(':');
            if (colonIdx > 0 && colonIdx < 50) {
                const label = rest.substring(0, colonIdx).trim();
                const value = rest.substring(colonIdx + 1).trim();
                content.push({
                    text: [
                        { text: `${prefix}${label} : `, bold: true, color: '#1c2143' },
                        { text: value, color: '#333333' }
                    ],
                    style: 'paragraph',
                    margin: [0, 8, 0, 4],
                });
            } else {
                content.push({
                    text: [
                        { text: prefix, bold: true, color: '#1c2143' },
                        { text: rest, color: '#333333' }
                    ],
                    style: 'paragraph',
                    margin: [0, 8, 0, 4],
                });
            }
        } else {
            content.push({ text: line, style: 'paragraph' });
        }
        i++;
    }

    // Anti-orphelins : regrouper titre + ligne + premier paragraphe
    const grouped = [];
    for (let i = 0; i < content.length; i++) {
        const item = content[i];
        const isTitle = item.style === 'sectionTitle' || item.style === 'subSectionTitle';
        const isCanvas = item.canvas !== undefined;

        if (isTitle || isCanvas) {
            const stack = [item];
            let j = i + 1;
            while (j < content.length) {
                if (content[j].canvas) { stack.push(content[j]); j++; }
                else { stack.push(content[j]); j++; break; }
            }
            if (stack.length > 1) {
                grouped.push({ stack, unbreakable: true });
                i = j - 1;
            } else {
                grouped.push(item);
            }
        } else {
            grouped.push(item);
        }
    }

    return grouped;
};

// --- COMPOSANT ---

const PdfGenerator = ({ messages, setShowForm, sentImageUrls = [], setShowModal, setIsOver, conversationId, refreshHistory, isCompleted }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [buttonStatus, setButtonStatus] = useState(false);
    const [isDownloaded, setIsDownloaded] = useState(false);
    const [serverLink, setServerLink] = useState(null);

    const { authToken } = useAuth();
    const userName = authToken.userName;
    const userId = authToken.userId;
    const userEmail = authToken.userEmail;

    const handlePdfGenerated = async (userId) => {
        try {
            await axios.post(`${API_URL}/update-pdf-status`, { userId });
            // Notifier le Header pour rafraichir les credits en temps reel
            window.dispatchEvent(new Event('credits-updated'));
        } catch (error) {
            console.error('Error updating PDF status:', error);
        }
    };

    const handleFinalizeConversation = async (newTitle, pdfLink) => {
        if (!conversationId) return;
        try {
            await axios.put(`${API_URL}/conversations/${conversationId}`, {
                title: newTitle,
                pdfUrl: pdfLink || null
            }, {
                headers: { 'auth-token': localStorage.getItem('auth-token') }
            });
            if (refreshHistory) await refreshHistory();
        } catch (error) {
            console.error("Erreur lors de la finalisation :", error);
        }
    };

    useEffect(() => {
        const phraseSpecifique = 'Début rapport';
        const messageContientPhrase = messages.some(message =>
            typeof message.content === 'string' && message.content.includes(phraseSpecifique)
        );
        setButtonStatus(messageContientPhrase);

        if (messageContientPhrase && !isDownloaded) {
            generatePdf();
            setIsDownloaded(true);
            handlePdfGenerated(userId);
        }

        if (isDownloaded && !isLoading) {
            let alertHtml = '<div style="font-family: sans-serif; margin-bottom: 10px;">Votre pré-rapport a été généré avec succès.</div>';
            if (serverLink) {
                alertHtml += `<div style="font-family: sans-serif; margin-top: 10px; font-size: 0.9em;">
                    <a href="${serverLink}" target="_blank" style="color: #Ceaa5b; text-decoration: underline;">Télécharger via le lien sécurisé</a>
                </div>`;
            }
            Swal.fire({
                title: "Pré-rapport généré !",
                html: alertHtml,
                icon: "success",
                confirmButtonColor: "#1c2143"
            });
            if (setIsOver) setIsOver(true);
        }
    }, [messages, isDownloaded, isLoading, serverLink]);


    const generatePdf = async () => {
        setIsLoading(true);

        // 1. Extraire le contenu entre //Debut rapport// et //Fin rapport//
        const lastMessages = messages.slice(Math.max(messages.length - 25, 0));
        let globalContentPreRapport = '';

        lastMessages.forEach((message) => {
            if (typeof message.content === 'string') {
                const messageLines = message.content.split("\n").map(cleanText);
                let isPreRapport = false;
                let contentPreRapport = '';

                messageLines.forEach(line => {
                    if (line.includes('Début rapport')) {
                        isPreRapport = true;
                        setButtonStatus(true);
                    } else if (line.includes('Fin rapport')) {
                        isPreRapport = false;
                    } else if (isPreRapport) {
                        contentPreRapport += line + '\n';
                    }
                });

                if (contentPreRapport) {
                    globalContentPreRapport += contentPreRapport + '\n';
                }
            }
        });

        // 2. Extraire infos client, mandant, partie adverse et dommage
        const { clientName, mandant, partieAdverse, dommage } = extractInfoFromMessages(messages);

        // 3. Construire les noms
        const date = new Date();
        const formatedDate = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

        // Titre dans la sidebar et le PDF
        let pdfTitle = 'Pré-rapport';
        if (clientName) pdfTitle += ` - ${clientName}`;
        if (dommage) pdfTitle += ` - ${dommage}`;

        // Nom de fichier (sans accents)
        const sanitize = (str) => str
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '_');
        const safeName = sanitize(clientName || userName);
        const safeDommage = dommage ? sanitize(dommage) : '';
        let fileName = `Pre-rapport_${safeName}`;
        if (safeDommage) fileName += `_${safeDommage}`;
        fileName += `.pdf`;

        // 4. Construire le contenu structure
        const structuredContent = buildStructuredContent(globalContentPreRapport);

        // 5. Document PDF
        const docDefinition = {
            pageSize: 'A4',
            pageMargins: [50, 45, 50, 55],
            footer: (currentPage, pageCount) => ({
                stack: [
                    {
                        canvas: [{ type: 'line', x1: 50, y1: 0, x2: 545, y2: 0, lineWidth: 0.5, lineColor: '#Ceaa5b' }],
                        margin: [0, 0, 0, 4],
                    },
                    {
                        text: [
                            { text: 'AI EXPERTYS', bold: true, fontSize: 7.5, color: '#1c2143' },
                            { text: ' - SIRET 981 340 003 00012 - Programmation informatique (6201Z)', fontSize: 6.5, color: '#888888' },
                        ],
                        alignment: 'center',
                        margin: [40, 0, 40, 1],
                    },
                    {
                        text: 'Chemin de Villeneuve, 66200 Corneilla-del-Vercol - contact@aiexpertys.fr',
                        alignment: 'center',
                        fontSize: 6.5,
                        color: '#888888',
                        margin: [40, 0, 40, 0],
                    },
                    {
                        text: `${currentPage} / ${pageCount}`,
                        alignment: 'center',
                        fontSize: 7,
                        color: '#aaaaaa',
                        margin: [0, 2, 0, 0],
                    },
                ],
            }),
            content: [
                // Logo
                { image: headerImage.headerImage, width: 120, alignment: 'center', margin: [0, 5, 0, 15] },

                // Titre principal
                { text: 'PRE-RAPPORT D\'EXPERTISE', style: 'mainTitle' },

                // Date
                { text: `du ${formatedDate}`, style: 'dateSubtitle' },

                // Ligne decorative sous le titre
                {
                    canvas: [{ type: 'line', x1: 50, y1: 0, x2: 395, y2: 0, lineWidth: 1.5, lineColor: '#Ceaa5b' }],
                    margin: [0, 8, 0, 15],
                },

                // Bloc informations client
                {
                    table: {
                        widths: [75, '*'],
                        body: [
                            [
                                { text: 'Client :', style: 'infoLabel' },
                                { text: clientName || '-', style: 'infoValue' }
                            ],
                            [
                                { text: 'Dommage :', style: 'infoLabel' },
                                { text: dommage || '-', style: 'infoValue' }
                            ],
                            [
                                { text: 'Expert :', style: 'infoLabel' },
                                { text: userName, style: 'infoValue' }
                            ],
                            [
                                { text: 'Date :', style: 'infoLabel' },
                                { text: formatedDate, style: 'infoValue' }
                            ],
                        ],
                    },
                    layout: {
                        hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 0.5 : 0.3,
                        vLineWidth: () => 0,
                        hLineColor: () => '#e0e0e0',
                        paddingLeft: () => 10,
                        paddingRight: () => 10,
                        paddingTop: () => 5,
                        paddingBottom: () => 5,
                        fillColor: (rowIndex) => rowIndex % 2 === 0 ? '#f7f8fa' : '#ffffff',
                    },
                    margin: [0, 0, 0, 25],
                },

                // Contenu structure du rapport
                ...structuredContent,
            ],
            styles: {
                mainTitle: {
                    fontSize: 20,
                    bold: true,
                    alignment: 'center',
                    color: '#1c2143',
                    margin: [0, 0, 0, 2],
                },
                dateSubtitle: {
                    fontSize: 11,
                    alignment: 'center',
                    color: '#666666',
                    margin: [0, 0, 0, 0],
                },
                infoLabel: {
                    fontSize: 9.5,
                    bold: true,
                    color: '#1c2143',
                },
                infoValue: {
                    fontSize: 9.5,
                    color: '#333333',
                },
                sectionTitle: {
                    fontSize: 13,
                    bold: true,
                    color: '#1c2143',
                    margin: [0, 18, 0, 3],
                },
                subSectionTitle: {
                    fontSize: 11,
                    bold: true,
                    color: '#333333',
                    margin: [8, 10, 0, 4],
                },
                paragraph: {
                    fontSize: 10,
                    lineHeight: 1.55,
                    alignment: 'justify',
                    color: '#333333',
                    margin: [0, 2, 0, 6],
                },
                image: {
                    margin: [0, 10, 0, 10],
                    alignment: 'center',
                },
            },
        };

        // 6. Ajouter les images si presentes
        if (sentImageUrls.length > 0) {
            try {
                const dataUrls = await Promise.all(sentImageUrls.map(url => convertImageToDataUrl(url)));
                docDefinition.content.push({
                    text: 'Annexes photographiques',
                    style: 'sectionTitle',
                    margin: [0, 20, 0, 2],
                });
                docDefinition.content.push({
                    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 395, y2: 0, lineWidth: 1, lineColor: '#Ceaa5b' }],
                    margin: [0, 0, 0, 10],
                });
                const images = dataUrls.map(url => ({ image: url, width: 350, style: 'image' }));
                docDefinition.content.push(...images);
            } catch (error) {
                console.error("Erreur images:", error);
            }
        }

        // 7. Generer UNE SEULE FOIS le base64 puis l'utiliser pour le download local ET l'envoi serveur
        if (pdfMake && typeof pdfMake.createPdf === 'function') {
            const pdfDocGenerator = pdfMake.createPdf(docDefinition);

            pdfDocGenerator.getBase64(async (base64) => {
                try {
                    // A. Download local depuis le meme base64
                    const byteCharacters = atob(base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

                    // B. Envoi serveur (meme base64 = fichier identique)
                    const response = await fetch(`${API_URL}/pdf`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            pdfData: base64,
                            userInfo: { userName, userId, userEmail, clientName, dommage }
                        })
                    });

                    const result = await response.json();
                    if (!response.ok || !result.success) throw new Error(result.message);

                    if (result.link) setServerLink(result.link);
                    await handleFinalizeConversation(pdfTitle, result.link);

                } catch (error) {
                    console.error("Erreur PDF:", error);
                } finally {
                    setIsLoading(false);
                }
            });
        } else {
            console.error("pdfMake non initialise");
            setIsLoading(false);
        }
    };

    const convertImageToDataUrl = (url) => {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.crossOrigin = 'Anonymous';
            img.onload = function () {
                const canvas = document.createElement('canvas');
                canvas.width = this.naturalWidth;
                canvas.height = this.naturalHeight;
                canvas.getContext('2d').drawImage(this, 0, 0);
                resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    return null;
};

export default PdfGenerator;
